import { Line as Outline } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import { Plane, Vector2 } from 'three';
import { Line2, LineSegments2 } from 'three/examples/jsm/Addons.js';
import { useShallow } from 'zustand/shallow';

import { LayerType } from 'app/modules/wallforming/components/scenes/camera-controls/types/layerType';
import { computeWorkingPlaneData } from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { PoiNavigationSource } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiNavigationSource';
import { isCornerSingleSidePoi } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

interface EdgeHighlightProps {
    selectedPoiIndex: number;
}

export const EdgeHighlight = ({ selectedPoiIndex }: EdgeHighlightProps) => {
    const size = useThree((state) => state.size);
    const shouldRealignCamera = usePlacementStore((state) => state.poiSelection.current.shouldRealignCamera);

    const { poi, mainWebGLRendererRef, mainCameraControlsRef, setFocusOnCorner, setWorkingPlane } = usePlacementStore(
        useShallow((state) => ({
            poi: state.pois[selectedPoiIndex]?.poi ?? null,
            mainWebGLRendererRef: state.mainWebGLRendererRef,
            mainCameraControlsRef: state.mainCameraControlsRef,
            setFocusOnCorner: state.setFocusOnCorner,
            setWorkingPlane: state.setWorkingPlane
        }))
    );

    const prevIndexRef = useRef<number | undefined>(undefined);
    const prevAlignmentRef = useRef<boolean | undefined>(undefined);
    const outLineRef = useRef<Line2 | LineSegments2 | null>(null);

    const cornerPoi = useMemo(() => {
        if (!poi || !isCornerSingleSidePoi(poi)) {
            return null;
        }
        return poi;
    }, [poi]);

    const edgePoints = useMemo(() => {
        if (!cornerPoi) {
            return [];
        }
        const { topPoint, bottomPoint } = cornerPoi;
        return [topPoint.x, topPoint.y, topPoint.z, bottomPoint.x, bottomPoint.y, bottomPoint.z];
    }, [cornerPoi]);

    useEffect(() => {
        if (
            selectedPoiIndex === undefined ||
            (selectedPoiIndex === prevIndexRef.current && shouldRealignCamera === prevAlignmentRef.current)
        ) {
            return;
        }

        if (mainCameraControlsRef.current && mainWebGLRendererRef.current && cornerPoi) {
            const state = usePlacementStore.getState();
            const { source, shouldRealignCamera } = state.poiSelection.current;

            if (shouldRealignCamera ?? (source !== undefined && !state.disableCameraAlignBySource[source])) {
                setFocusOnCorner(mainCameraControlsRef, cornerPoi, mainWebGLRendererRef.current.domElement);
            }

            if (source !== undefined) {
                state.setDisableCameraAlign(PoiNavigationSource.PointerSelection, false);
            }
            prevIndexRef.current = selectedPoiIndex;
            prevAlignmentRef.current = shouldRealignCamera;
        }
    }, [
        selectedPoiIndex,
        shouldRealignCamera,
        mainCameraControlsRef,
        mainWebGLRendererRef,
        setFocusOnCorner,
        cornerPoi
    ]);

    useEffect(() => {
        if (!outLineRef.current) {
            return;
        }

        outLineRef.current.geometry.computeVertexNormals();
        const { position, normal } = computeWorkingPlaneData(outLineRef.current.geometry);
        const plane = new Plane().setFromNormalAndCoplanarPoint(normal, position);
        setWorkingPlane(plane);
    }, [setWorkingPlane, outLineRef]);

    if (!cornerPoi || edgePoints.length === 0) {
        return null;
    }

    return (
        <Outline
            ref={outLineRef}
            color={'#649fda'}
            dashed={false}
            layers={[LayerType.DEFAULT]}
            lineWidth={5}
            points={edgePoints}
            polygonOffset={true}
            polygonOffsetFactor={-10}
            polygonOffsetUnits={-4}
            renderOrder={1000}
            resolution={new Vector2(size.width, size.height)}
            transparent={false}
        />
    );
};
